/**
 * @file useCanEditPhotoType Hook
 * @description 사진 타입 편집 권한 체크 훅
 * @see docs/plans/loading/05-implementation.md - Section 32
 */

import { useMemo } from 'react';
import { useAppSelector } from '@/src/store';
import type { ExporterUserType } from '@/src/store/apis/auth/interface';

/**
 * 사진 타입 편집이 허용된 역할
 */
const ALLOWED_ROLES: ExporterUserType[] = [
  'CORPORATE_MANAGER', // 기업관리자
  'MIDDLE_MANAGER', // 중간관리자
];

/**
 * 사진 타입 편집 권한 체크 훅
 *
 * @description
 * 사용자의 역할에 따라 사진 타입 편집 권한을 반환합니다.
 *
 * **허용 역할:**
 * - CORPORATE_MANAGER (기업관리자)
 * - MIDDLE_MANAGER (중간관리자)
 *
 * **차단 역할:**
 * - MANAGER (실무자) → 수정 버튼 히든
 *
 * @security
 * ⚠️ **클라이언트 사이드 체크만으로는 보안이 보장되지 않습니다.**
 * - 백엔드 API에서도 반드시 동일한 권한 검증을 수행해야 합니다.
 * - 이 훅은 UI 표시 여부 결정용이며, 실제 보안은 서버에서 담당합니다.
 *
 * @returns {boolean} 편집 권한 여부
 *
 * @example
 * ```tsx
 * const TaskAddPage: React.FC = () => {
 *   const canEditPhotoType = useCanEditPhotoType();
 *
 *   return (
 *     <ContainerPhotoSection>
 *       <SectionHeader>
 *         <Title>{t('task:containerPhotos')}</Title>
 *         {canEditPhotoType && (
 *           <Button type="link" onClick={() => setIsModalOpen(true)}>
 *             {t('common:edit')}
 *           </Button>
 *         )}
 *       </SectionHeader>
 *     </ContainerPhotoSection>
 *   );
 * };
 * ```
 */
export const useCanEditPhotoType = (): boolean => {
  const user = useAppSelector((state) => state.auth.user);

  return useMemo(() => {
    if (!user) return false;

    // ExporterUserDto 타입 가드: AccountSignInResponseDto에는 exporterUserType이 없음
    if (!('exporterUserType' in user)) return false;

    return ALLOWED_ROLES.includes(user.exporterUserType);
  }, [user]);
};

/**
 * 사진 타입 편집 권한 체크 (순수 함수)
 *
 * @description 훅 외부에서 권한 체크가 필요한 경우 사용
 *
 * @param userType - 사용자 역할 타입
 * @returns 편집 권한 여부
 */
export const canEditPhotoType = (
  userType: ExporterUserType | undefined | null
): boolean => {
  if (!userType) return false;
  return ALLOWED_ROLES.includes(userType);
};
